import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from '@tanstack/react-form'
import {
  Container,
  Box,
  Card,
  TextField,
  Skeleton,
  Typography,
} from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchTrainingSeries,
  fetchLinkedServices,
  fetchCourses,
  selectTrainingSeriesById,
  selectCourses,
  selectCoursesStatus,
  selectLinkedServicesStatus,
  selectTrainingSeriesStatus,
} from '../manage.slice'
import CoursesDataGrid from './components/courses-data-grid'
import { TrainingSeriesFormData, trainingSeriesSchema } from './validation'
import { paperInputSx } from '../styles'
import {
  transformCoursesToRows,
  extractCategories,
  filterCourses,
} from './utils/course-transforms'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import CreateFooterActionButtons from '../components/cancel-save-buttons'
import { updateTrainingSeries } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { TRAINING_SERIES_URLS } from '../constants'

export default function TrainingSeriesEditV4(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { trainingSeriesID } = useParams<{ trainingSeriesID: string }>()

  let { showError, NotificationComponent } = useNotifications()

  let [selectedCourses, setSelectedCourses] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  let [initialCourses, setInitialCourses] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  let [categoryFilter, setCategoryFilter] = useState('')
  let [searchQuery, setSearchQuery] = useState('')

  let trainingSeries = useAppSelector(
    selectTrainingSeriesById(trainingSeriesID || ''),
  )
  let fetchTrainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let courses = useAppSelector(selectCourses)
  let fetchCoursesStatus = useAppSelector(selectCoursesStatus)
  let fetchLinkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let courseRows = useMemo(() => transformCoursesToRows(courses), [courses])
  let availableCategories = useMemo(
    () => extractCategories(courseRows),
    [courseRows],
  )

  // Sort courses to show initially selected courses first (optimized with Set for O(1) lookup)
  let sortedCourseRows = useMemo(() => {
    let initialSet = initialCourses.ids
    return courseRows.toSorted((a, b) => {
      let aInitial = initialSet.has(a.id)
      let bInitial = initialSet.has(b.id)
      if (aInitial && !bInitial) return -1
      if (!aInitial && bInitial) return 1
      return 0
    })
  }, [courseRows, initialCourses])

  // Filter courses (always include selected courses)
  let filteredCourseRows = useMemo(
    () =>
      filterCourses(
        sortedCourseRows,
        Array.from(selectedCourses.ids) as string[],
        categoryFilter,
        searchQuery,
      ),
    [sortedCourseRows, categoryFilter, searchQuery, selectedCourses],
  )

  let linkedServiceName = useLinkedServiceName(trainingSeries?.linkedServiceID)

  let form = useForm({
    defaultValues: {
      name: trainingSeries?.name || '',
      courseIDs: trainingSeries?.courseIDs || [],
    } as TrainingSeriesFormData,
    validators: {
      onChange: trainingSeriesSchema,
    },
    onSubmit: async ({ value }) => {
      if (!trainingSeries || !trainingSeries.id) return

      let payload = {
        id: trainingSeries.id,
        name: value.name,
        linkedServiceID: trainingSeries.linkedServiceID,
        courseIDs: value.courseIDs,
      }

      await updateTrainingSeries(payload)
        .then(() => dispatch(fetchTrainingSeries()))
        .then(() => {
          navigate(`${TRAINING_SERIES_URLS.INDEX}/${trainingSeriesID}`, {
            state: {
              successMessage: `Training series "${trainingSeries.name}" updated successfully.`,
            },
          })
        })
        .catch((err: any) => {
          let errorMessage = `Error updating training series "${trainingSeries.name}".`
          // eslint-disable-next-line no-console
          console.error(errorMessage, 'Payload: ', payload, 'Error', err)
          showError(errorMessage)
        })
    },
  })

  let handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelectedCourses(newSelection)

      // Calculate selected IDs based on selection model type
      let selectedIDs =
        newSelection.type === 'exclude'
          ? (() => {
              // Select all: get all visible IDs except excluded ones
              let excludedIDs = newSelection.ids
                ? (Array.from(newSelection.ids) as string[])
                : []
              return filteredCourseRows
                .map((row) => row.id)
                .filter((id) => !excludedIDs.includes(id))
            })()
          : newSelection.ids
            ? (Array.from(newSelection.ids) as string[])
            : []

      form.setFieldValue('courseIDs', selectedIDs)
      form.validate('change')
    },
    [filteredCourseRows, form],
  )

  let handleCancel = useCallback(() => {
    navigate(`${TRAINING_SERIES_URLS.INDEX}/${trainingSeriesID}`)
  }, [navigate, trainingSeriesID])

  let handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setCategoryFilter('')
  }, [])

  let isLoading =
    fetchTrainingSeriesStatus === 'pending' ||
    fetchCoursesStatus === 'pending' ||
    fetchLinkedServicesStatus === 'pending'

  useAutoFetch(fetchTrainingSeriesStatus, fetchTrainingSeries)
  useAutoFetch(fetchLinkedServicesStatus, fetchLinkedServices)
  useAutoFetch(fetchCoursesStatus, fetchCourses)

  useEffect(() => {
    if (trainingSeries) {
      form.setFieldValue('name', trainingSeries.name)
      form.setFieldValue('courseIDs', trainingSeries.courseIDs)
      let selectionModel: GridRowSelectionModel = {
        type: 'include',
        ids: new Set(trainingSeries.courseIDs || []),
      }
      setSelectedCourses(selectionModel)
      setInitialCourses(selectionModel)
    }
  }, [trainingSeries])

  return (
    <Container maxWidth="xl" className="py-6">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        aria-label="Edit training series form"
      >
        <Box className="mb-4">
          <PageTitleWithLinkedService
            pageTitle={
              trainingSeries?.name
                ? `Edit ${trainingSeries.name}`
                : 'Edit Training Series'
            }
            linkedServiceName={linkedServiceName ?? ''}
            moduleName="Training Series"
            handleBackClick={() =>
              navigate(`${TRAINING_SERIES_URLS.INDEX}/${trainingSeriesID}`)
            }
            isLoading={isLoading}
          />
        </Box>

        <Card className="p-6 mb-6">
          <Typography variant="h6" component="h2" className="mb-4">
            Training Series Information
          </Typography>
          <form.Field name="name">
            {(field) =>
              isLoading || !trainingSeries ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <TextField
                  fullWidth
                  required
                  label="Name"
                  placeholder="Enter training series name..."
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta)
                      ? getZodValidationError(field.state.meta.errors)
                      : undefined
                  }
                  aria-label="Training series name"
                  aria-invalid={shouldShowZodFieldError(field.state.meta)}
                  sx={paperInputSx}
                />
              )
            }
          </form.Field>
        </Card>

        <form.Field name="courseIDs">
          {(field) => (
            <CoursesDataGrid
              courseRows={filteredCourseRows}
              selectedCourses={selectedCourses}
              onSelectionChange={handleSelectionChange}
              searchQuery={searchQuery}
              categoryFilter={categoryFilter}
              availableCategories={availableCategories}
              onSearchChange={setSearchQuery}
              onCategoryChange={setCategoryFilter}
              onResetFilters={handleResetFilters}
              isLoading={isLoading}
              field={field}
              editing={true}
              title="Select Courses"
            />
          )}
        </form.Field>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <CreateFooterActionButtons
              moduleName="Training Series"
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              isDisabled={isLoading || isSubmitting}
              handleSave={form.handleSubmit}
              handleCancel={handleCancel}
            />
          )}
        </form.Subscribe>
      </form>
      {NotificationComponent}
    </Container>
  )
}
